import React, { createContext, useMemo, useCallback, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { Text } from '@atlaskit/primitives/compiled/text';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import type { AnalyticsEventPayload } from '@atlaskit/analytics-next/AnalyticsEvent';
import type UIAnalyticsEvent from '@atlaskit/analytics-next/UIAnalyticsEvent';

import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import { fg } from '@confluence/feature-gating';
import { cfetch } from '@confluence/network';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { AccessStatus } from '@confluence/session-data/entry-points/SiteInformationQuery.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { confluenceLocalStorageInstance as localStorage } from '@confluence/storage-manager/entry-points/storageInstance.ts';
import GetUserAccessQuery from './__generated__/GetUserAccessQuery.graphql.ts';
import type {
	GetUserAccessQuery as GetUserAccessQueryType,
	GetUserAccessQueryVariables,
} from './__types__/GetUserAccessQuery.ts';
import { LicenseStatus } from './__types__/GetUserAccessQuery.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { queryInterop } from '@confluence/apollo-relay-interop/entry-points/query.ts';

const INVITE_CAPABILITIES_URL = '/gateway/api/v3/invitations/capabilities';
const INVITE_CAPABILITIES_DIRECT_INVITE_MODE_KEY =
	'confluence_invite_capabilities_direct_invite_mode';

type XProductUserInviteContextType = {
	isEligibleXProductUserInvite?: () => Promise<boolean>;
	onXProductUserInviteClick?: (name: string, isSuccess: boolean) => void;
};

type XProductUserInviteProviderProps = {
	flags: FlagsStateContainer;
	children: React.ReactNode;
};

const i18n = defineMessages({
	inviteSuccessTitle: {
		id: 'fabric-media-support.mentions-provider.x-product-user-invite.success.title',
		description: 'Success title message when a user is invited to a product',
		defaultMessage: 'Successfully added',
	},
	inviteSuccessDescription: {
		id: 'fabric-media-support.mentions-provider.x-product-user-invite.success.description',
		description: 'Success message when a user is invited to a product',
		defaultMessage: 'We sent an email to <b>{name}</b> confirming you added them to Confluence.',
	},
});

export const XProductUserInviteContext: React.Context<XProductUserInviteContextType> =
	createContext<XProductUserInviteContextType>({});

const hasDirectInviteCapabilities = async (
	cloudId: string,
	createAnalyticsEvent: (payload: AnalyticsEventPayload) => UIAnalyticsEvent,
): Promise<boolean> => {
	try {
		const canUserAccessJira = (await hasJiraSoftwareAccess(cloudId)) ?? false;
		let directInviteMode: string | undefined;
		let invitePendingApprovalMode: string | undefined;
		if (canUserAccessJira) {
			//Call invite capabilities endpoint only if directInviteMode value is not available from local storage
			directInviteMode = localStorage.getItem(INVITE_CAPABILITIES_DIRECT_INVITE_MODE_KEY);
			if (!directInviteMode) {
				const response = await cfetch(
					`${INVITE_CAPABILITIES_URL}?resource=ari:cloud:platform::site/${cloudId}`,
					{
						method: 'GET',
						headers: {
							ACCEPT: 'application/json',
						},
					},
				);

				if (!response.ok) {
					throw new Error(`Network response error.  HTTP status: ${response.status}`);
				}

				const data = await response.json();
				for (const item of data) {
					if (item?.resourceARI?.includes('confluence')) {
						directInviteMode = item?.directInvite?.mode;
						invitePendingApprovalMode = item?.invitePendingApproval?.mode;
						break;
					}
				}

				createAnalyticsEvent &&
					createAnalyticsEvent({
						type: 'sendOperationalEvent',
						data: {
							source: 'editor',
							action: 'fetched',
							actionSubject: 'inviteCapabilitiesForXProductUserInvite',
							attributes: {
								directInviteMode,
								invitePendingApprovalMode,
							},
						},
					}).fire();
				if (!!directInviteMode)
					localStorage.setItem(INVITE_CAPABILITIES_DIRECT_INVITE_MODE_KEY, directInviteMode, 86400);
			}
		}
		return directInviteMode === 'ANYONE';
	} catch (error) {
		const errorMessage = error instanceof Error ? error.message : String(error);
		createAnalyticsEvent &&
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					source: 'editor',
					action: 'errored',
					actionSubject: 'inviteCapabilitiesForXProductUserInvite',
					attributes: {
						errorMessage,
					},
				},
			}).fire();
		return false;
	}
};

const hasJiraSoftwareAccess = async (cloudId: string): Promise<boolean | null | undefined> => {
	return queryInterop<GetUserAccessQueryType, GetUserAccessQueryVariables>({
		query: GetUserAccessQuery,
		variables: {
			resourceId: `ari:cloud:jira::site/${cloudId}`,
			permissionId: 'write',
		},
	})
		.then(({ data }) => {
			const hasJira = data?.confluence_tenantContext?.licensedProducts.some(
				(product) =>
					product?.productKey?.toLowerCase().includes('jira-software') &&
					product?.licenseStatus === LicenseStatus.ACTIVE,
			);
			return !!hasJira && data?.permitted;
		})
		.catch((error) => {
			getMonitoringClient().submitError(error, {
				attribution: Attribution.FLYWHEEL_ENGAGEMENT,
			});
			markErrorAsHandled(error);
			return false;
		});
};

export const XProductUserInviteProviderComponent = ({
	children,
	flags,
}: XProductUserInviteProviderProps): React.JSX.Element => {
	const { accessStatus, cloudId } = useSessionData();
	const { formatMessage } = useIntl();
	const promiseRef = useRef<Promise<boolean>>();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const isEligibleXProductUserInvite = useCallback(() => {
		//check the eligibility criteria only once per SPA
		if (promiseRef.current) {
			return promiseRef.current;
		}

		promiseRef.current = (async () => {
			try {
				if (
					// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
					!fg('cc_virality_x_product_user_invite_kill_switch') ||
					!(
						accessStatus === AccessStatus.LICENSED_ADMIN_ACCESS ||
						accessStatus === AccessStatus.LICENSED_USE_ACCESS
					)
				) {
					return false;
				}
				return await hasDirectInviteCapabilities(cloudId, createAnalyticsEvent);
			} catch {
				return false;
			}
		})();

		return promiseRef.current;
	}, [accessStatus, cloudId, createAnalyticsEvent]);

	const showSuccessFlag = useCallback(
		(name: string, isSuccess: boolean): void => {
			if (isSuccess) {
				void flags.showFlag({
					id: `x-product-user-invite-success`,
					title: formatMessage(i18n.inviteSuccessTitle),
					description: formatMessage(i18n.inviteSuccessDescription, {
						name,
						b: (chunks: React.ReactNode[]) => <Text weight="bold">{chunks}</Text>,
					}),
					type: 'success-circle',
				});
			}
		},
		[flags, formatMessage],
	);

	const value = useMemo(
		() => ({
			isEligibleXProductUserInvite,
			onXProductUserInviteClick: (name: string, isSuccess: boolean) => {
				showSuccessFlag(name, isSuccess);
			},
		}),
		[isEligibleXProductUserInvite, showSuccessFlag],
	);

	return (
		<XProductUserInviteContext.Provider value={value}>
			{children}
		</XProductUserInviteContext.Provider>
	);
};

export const XProductUserInviteProvider: React.ComponentType<
	Omit<XProductUserInviteProviderProps, 'flags'>
> = withFlags(XProductUserInviteProviderComponent);
